import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database, storage } from '../../firebase';
import { ref, get, set, push, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import {
  PlusIcon, TrashIcon, PencilIcon, XMarkIcon,
  PhotoIcon, ArrowUpTrayIcon, SparklesIcon,
  CalendarIcon, ClockIcon,
  EyeIcon, EyeSlashIcon,
  ChevronDownIcon, ChevronUpIcon, BoltIcon,
  UserIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const DAYS_OF_WEEK = [
  { key: 'monday',    label: 'Mon' },
  { key: 'tuesday',   label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday',  label: 'Thu' },
  { key: 'friday',    label: 'Fri' },
  { key: 'saturday',  label: 'Sat' },
  { key: 'sunday',    label: 'Sun' },
];

const SCHEDULE_TYPES = [
  { key: 'recurring',      label: 'Recurring Weekly', desc: 'Repeats on selected days every week',   icon: <BoltIcon     className="h-5 w-5" /> },
  { key: 'specific_date',  label: 'Specific Date',    desc: 'One-time on a single date',            icon: <CalendarIcon className="h-5 w-5" /> },
  { key: 'specific_dates', label: 'Multiple Dates',   desc: 'Multiple specific dates at same time', icon: <SparklesIcon className="h-5 w-5" /> },
];

// ─── SWAL Z-INDEX ─────────────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'swal-topz' } };
if (typeof document !== 'undefined' && !document.getElementById('swal-topz-style')) {
  const s = document.createElement('style');
  s.id = 'swal-topz-style';
  s.textContent = '.swal-topz{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatScheduleSummary = (schedule) => {
  if (schedule.type === 'recurring') {
    const days = (schedule.days || [])
      .map(d => DAYS_OF_WEEK.find(dw => dw.key === d)?.label)
      .filter(Boolean)
      .join(', ');
    return `${days} at ${schedule.time}`;
  }
  if (schedule.type === 'specific_date') {
    return `${new Date(schedule.date + 'T00:00:00').toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })} at ${schedule.time}`;
  }
  if (schedule.type === 'specific_dates') {
    const count = (schedule.dates || []).length;
    return `${count} date${count !== 1 ? 's' : ''} at ${schedule.time}`;
  }
  return '';
};

const EMPTY_ACTIVITY = {
  name: '',
  title: '',
  trainers: [],
  description: [],
  whatToExpect: [],
  pricePerVisit: '',
  active: true,
  photos: [],
  schedules: [],
};

const EMPTY_SCHEDULE_FORM = {
  type: 'recurring',
  label: '',
  days: [],
  date: '',
  dates: [],
  time: '',
  active: true,
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const ActivityManagement = () => {
  const { userData, currentUser } = useAuth();

  const userRole     = (userData?.personalInfo?.role || '').toLowerCase();
  const isAdmin      = userRole === 'admin';
  const isSuperAdmin = userRole === 'superadmin';
  const isAuthorized = isAdmin || isSuperAdmin;

  const requireAuth = () => {
    if (!isAuthorized) {
      toast('error', 'Access Denied', 'Only Admins and Super-Admins can perform this action.');
      return false;
    }
    return true;
  };

  const [activities,       setActivities]       = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [isModalOpen,      setIsModalOpen]      = useState(false);
  const [editingActivity,  setEditingActivity]  = useState(null);
  const [activityForm,     setActivityForm]     = useState(EMPTY_ACTIVITY);
  const [uploading,        setUploading]        = useState(false);

  useEffect(() => { fetchActivities(); }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const snap = await get(ref(database, 'activities'));
      setActivities(snap.exists()
        ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v }))
        : []);
    } catch (e) {
      console.error(e);
      toast('error', 'Error', 'Failed to load activities');
    } finally { setLoading(false); }
  };

  const openAdd = () => {
    if (!requireAuth()) return;
    setEditingActivity(null);
    setActivityForm({ ...EMPTY_ACTIVITY, description: [], photos: [], schedules: [], whatToExpect: [], trainers: [], pricePerVisit: '' });
    setIsModalOpen(true);
  };

  const openEdit = (activity) => {
    if (!requireAuth()) return;
    setEditingActivity(activity);
    setActivityForm({
      name:         activity.name         || '',
      title:        activity.title        || '',
      trainers:     activity.trainers     || [],
      description:  activity.description  || [],
      whatToExpect: activity.whatToExpect || [],
      pricePerVisit: activity.pricePerVisit || '',
      active:       activity.active !== undefined ? activity.active : true,
      photos:       activity.photos       || [],
      schedules:    activity.schedules    || [],
    });
    setIsModalOpen(true);
  };

  const saveActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.name.trim())  return toast('error', 'Validation Error', 'Activity name is required');
    if (!activityForm.title.trim()) return toast('error', 'Validation Error', 'Activity title is required');

    setLoading(true);
    try {
      const now = Date.now();
      const payload = {
        name:         activityForm.name.trim(),
        title:        activityForm.title.trim(),
        trainers:     activityForm.trainers     || [],
        description:  activityForm.description  || [],
        whatToExpect: activityForm.whatToExpect || [],
        pricePerVisit: parseFloat(activityForm.pricePerVisit) || 0,
        active:       activityForm.active,
        photos:       activityForm.photos    || [],
        schedules:    activityForm.schedules || [],
        updatedAt:    now,
        createdAt:    editingActivity ? (editingActivity.createdAt || now) : now,
        createdBy:    currentUser?.uid || '',
        createdByName: userData?.personalInfo?.name || 'System',
      };

      if (editingActivity) {
        await update(ref(database, `activities/${editingActivity.id}`), payload);
        toast('success', 'Updated!', 'Activity updated successfully');
      } else {
        const nr = push(ref(database, 'activities'));
        await set(nr, { ...payload, id: nr.key });
        toast('success', 'Created!', 'Activity created successfully');
      }
      setIsModalOpen(false);
      fetchActivities();
    } catch (err) {
      console.error(err);
      toast('error', 'Error', 'Failed to save activity');
    } finally { setLoading(false); }
  };

  const deleteActivity = async (activity) => {
    if (!requireAuth()) return;
    const ok = await Swal.fire({
      title: `Delete "${activity.title}"?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#800020',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      ...SWAL_Z,
    });
    if (!ok.isConfirmed) return;
    await remove(ref(database, `activities/${activity.id}`));
    toast('success', 'Deleted');
    fetchActivities();
  };

  const toggleActive = async (activity) => {
    if (!requireAuth()) return;
    try {
      await update(ref(database, `activities/${activity.id}`), { active: !activity.active });
      fetchActivities();
      toast('success', activity.active ? 'Deactivated' : 'Activated');
    } catch (e) { toast('error', 'Error', 'Failed to update status'); }
  };

  const uploadActivityPhotos = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const sr = storageRef(storage, `activities/${Date.now()}_${f.name}`);
        await uploadBytes(sr, f);
        urls.push(await getDownloadURL(sr));
      }
      setActivityForm(p => ({ ...p, photos: [...p.photos, ...urls] }));
    } catch (e) { toast('error', 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900">Activity Management</h1>
              {userRole && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                  style={{
                    background: isSuperAdmin ? '#1a1a2e' : isAdmin ? '#fef2f2' : '#f3f4f6',
                    color:      isSuperAdmin ? '#fff'    : isAdmin ? '#800020' : '#6b7280',
                    border:     `1px solid ${isSuperAdmin ? '#1a1a2e' : isAdmin ? '#fcd5de' : '#e5e7eb'}`,
                  }}>
                  {isSuperAdmin ? '⚡ Super Admin' : isAdmin ? '🛡 Admin' : '👁 Read Only'}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1">
              {isAuthorized
                ? 'Manage gym activities, schedules and visibility'
                : 'You have read-only access — contact an Admin to make changes'}
            </p>
          </div>
          {isAuthorized && (
            <button onClick={openAdd}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all text-sm"
              style={{ background: 'linear-gradient(135deg,#800020 0%,#b30000 100%)' }}>
              <PlusIcon className="h-5 w-5" />
              Add Activity
            </button>
          )}
        </div>

        {/* ── GRID ── */}
        {loading && activities.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-block w-10 h-10 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3 text-sm">Loading activities…</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <SparklesIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No activities yet</p>
            {isAuthorized && (
              <button onClick={openAdd} className="mt-3 text-sm font-semibold" style={{ color: '#800020' }}>
                + Create your first activity
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {activities.map(activity => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  isAuthorized={isAuthorized}
                  onEdit={() => openEdit(activity)}
                  onDelete={() => deleteActivity(activity)}
                  onToggleActive={() => toggleActive(activity)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <ActivityModal
            activityForm={activityForm}
            setActivityForm={setActivityForm}
            editing={!!editingActivity}
            loading={loading}
            uploading={uploading}
            uploadActivityPhotos={uploadActivityPhotos}
            onSubmit={saveActivity}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY CARD
// ═══════════════════════════════════════════════════════════════════════════════
const ActivityCard = ({ activity, isAuthorized, onEdit, onDelete, onToggleActive }) => {
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const photo = activity.photos?.[0];

  const activeSchedules   = (activity.schedules || []).filter(s => s.active !== false);
  const inactiveSchedules = (activity.schedules || []).filter(s => s.active === false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      className={`bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border overflow-hidden ${!activity.active ? 'opacity-75' : ''}`}
      style={{ borderColor: activity.active ? '#e5e7eb' : '#d1d5db' }}>

      {/* Photo */}
      <div className="relative bg-gray-100" style={{ height: 180 }}>
        {photo
          ? <img src={photo} alt={activity.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center">
              <PhotoIcon className="h-12 w-12 text-gray-200" />
            </div>
        }
        {!activity.active && (
          <div className="absolute inset-0 bg-gray-900/20 flex items-center justify-center">
            <span className="px-3 py-1 bg-gray-800/70 text-white text-xs font-bold rounded-full backdrop-blur-sm">INACTIVE</span>
          </div>
        )}
        {activity.photos?.length > 1 && (
          <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full backdrop-blur-sm">
            +{activity.photos.length - 1} photos
          </span>
        )}
        {isAuthorized && (
          <div className="absolute top-2 right-2 flex gap-1.5">
            <button onClick={onToggleActive}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-white shadow transition-all hover:scale-110"
              style={{ background: activity.active ? 'rgba(107,114,128,0.85)' : 'rgba(22,163,74,0.85)' }}
              title={activity.active ? 'Deactivate' : 'Activate'}>
              {activity.active ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
            </button>
            <button onClick={onEdit}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-white shadow transition-all hover:scale-110"
              style={{ background: 'rgba(26,26,46,0.85)' }} title="Edit">
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
            <button onClick={onDelete}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-white shadow transition-all hover:scale-110"
              style={{ background: 'rgba(220,38,38,0.85)' }} title="Delete">
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-gray-900 leading-tight">{activity.title}</h3>
          <span className={`flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${activity.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {activity.active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-2">{activity.name}</p>

        {/* Trainers */}
        {activity.trainers?.length > 0 && (
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            <UserIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            {activity.trainers.slice(0, 3).map((t, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t}</span>
            ))}
            {activity.trainers.length > 3 && (
              <span className="text-xs text-gray-400">+{activity.trainers.length - 3} more</span>
            )}
          </div>
        )}

        {/* Price per visit */}
        {activity.pricePerVisit > 0 && (
          <div className="mb-3">
            <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ background: '#fef2f2', color: '#800020' }}>
              KES {Number(activity.pricePerVisit).toLocaleString()} / visit
            </span>
          </div>
        )}

        {/* Description preview */}
        {activity.description?.length > 0 && (
          <div className="mb-3 space-y-1">
            {activity.description.slice(0, 1).map((sec, i) => (
              <div key={i}>
                {sec.title   && <p className="text-sm font-semibold text-gray-800">{sec.title}</p>}
                {sec.content && <p className="text-sm text-gray-500 line-clamp-2">{sec.content}</p>}
              </div>
            ))}
          </div>
        )}

        {/* What to expect preview */}
        {activity.whatToExpect?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">What to Expect</p>
            <div className="space-y-1">
              {activity.whatToExpect.slice(0, 2).map((item, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                  <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="line-clamp-1">{item}</span>
                </div>
              ))}
              {activity.whatToExpect.length > 2 && (
                <p className="text-xs text-gray-400 ml-5">+{activity.whatToExpect.length - 2} more</p>
              )}
            </div>
          </div>
        )}

        {/* Schedules collapsible */}
        {activity.schedules?.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <button onClick={() => setSchedulesOpen(o => !o)}
              className="w-full flex items-center justify-between text-left">
              <div className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4" style={{ color: '#800020' }} />
                <span className="text-xs font-semibold text-gray-600">
                  {activeSchedules.length} active
                  {inactiveSchedules.length > 0 && (
                    <span className="ml-1 text-gray-400">· {inactiveSchedules.length} inactive</span>
                  )}
                </span>
              </div>
              {schedulesOpen ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
            </button>
            <AnimatePresence>
              {schedulesOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden">
                  <div className="mt-2 space-y-1.5">
                    {activity.schedules.map((sch, i) => (
                      <div key={i} className={`flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5 ${sch.active === false ? 'bg-gray-50 opacity-60' : 'bg-gray-50'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sch.active === false ? 'bg-gray-400' : 'bg-green-500'}`} />
                        <span className="font-medium flex-shrink-0 text-gray-700">
                          {sch.label || SCHEDULE_TYPES.find(t => t.key === sch.type)?.label}
                        </span>
                        <span className="text-gray-400 truncate">{formatScheduleSummary(sch)}</span>
                        {sch.active === false && (
                          <span className="ml-auto flex-shrink-0 text-gray-400 text-xs">off</span>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Created: {new Date(activity.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY MODAL
// ═══════════════════════════════════════════════════════════════════════════════
const ActivityModal = ({
  activityForm, setActivityForm,
  editing, loading, uploading,
  uploadActivityPhotos,
  onSubmit, onClose,
}) => {
  const fileRef = useRef();

  // ── Description state ─────────────────────────────────────────────────────
  const [descTitle,      setDescTitle]      = useState('');
  const [descContent,    setDescContent]    = useState('');
  const [editingDescIdx, setEditingDescIdx] = useState(null);

  // ── Trainers state ────────────────────────────────────────────────────────
  const [trainerInput, setTrainerInput] = useState('');

  // ── What to Expect state ──────────────────────────────────────────────────
  const [expectInput, setExpectInput] = useState('');

  // ── Schedule builder state ────────────────────────────────────────────────
  const [scheduleForm, setScheduleForm] = useState({ ...EMPTY_SCHEDULE_FORM });
  const [dateInput,    setDateInput]    = useState('');

  // ── Description handlers ──────────────────────────────────────────────────
  const addDescSection = () => {
    if (!descTitle.trim() && !descContent.trim())
      return toast('error', 'Validation Error', 'Please add either a title or content');
    setActivityForm(p => ({
      ...p,
      description: [...p.description, { title: descTitle.trim(), content: descContent.trim() }],
    }));
    setDescTitle(''); setDescContent(''); setEditingDescIdx(null);
  };

  const updateDescSection = () => {
    if (!descTitle.trim() && !descContent.trim())
      return toast('error', 'Validation Error', 'Please add either a title or content');
    const updated = [...activityForm.description];
    updated[editingDescIdx] = { title: descTitle.trim(), content: descContent.trim() };
    setActivityForm(p => ({ ...p, description: updated }));
    setDescTitle(''); setDescContent(''); setEditingDescIdx(null);
  };

  const editDescSection = (idx) => {
    setDescTitle(activityForm.description[idx].title);
    setDescContent(activityForm.description[idx].content);
    setEditingDescIdx(idx);
  };

  const removeDescSection = (idx) =>
    setActivityForm(p => ({ ...p, description: p.description.filter((_, i) => i !== idx) }));

  // ── Trainers handlers ────────────────────────────────────────────────────
  const addTrainer = () => {
    if (!trainerInput.trim()) return;
    setActivityForm(p => ({ ...p, trainers: [...p.trainers, trainerInput.trim()] }));
    setTrainerInput('');
  };

  const removeTrainer = (idx) =>
    setActivityForm(p => ({ ...p, trainers: p.trainers.filter((_, i) => i !== idx) }));

  // ── What to Expect handlers ───────────────────────────────────────────────
  const addExpectItem = () => {
    if (!expectInput.trim()) return;
    setActivityForm(p => ({ ...p, whatToExpect: [...p.whatToExpect, expectInput.trim()] }));
    setExpectInput('');
  };

  const removeExpectItem = (idx) =>
    setActivityForm(p => ({ ...p, whatToExpect: p.whatToExpect.filter((_, i) => i !== idx) }));

  // ── Schedule handlers ─────────────────────────────────────────────────────
  const toggleDay = (day) =>
    setScheduleForm(p => ({
      ...p,
      days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day],
    }));

  const addDateToList = () => {
    if (!dateInput) return;
    if (!scheduleForm.dates.includes(dateInput))
      setScheduleForm(p => ({ ...p, dates: [...p.dates, dateInput] }));
    setDateInput('');
  };

  const addSchedule = () => {
    const { type, label, days, date, dates, time, active } = scheduleForm;
    if (!time)                                           return toast('error', 'Time required',  'Please set a time for this schedule');
    if (type === 'recurring'      && days.length  === 0) return toast('error', 'Days required',  'Select at least one day');
    if (type === 'specific_date'  && !date)              return toast('error', 'Date required',  'Please pick a date');
    if (type === 'specific_dates' && dates.length === 0) return toast('error', 'Dates required', 'Please add at least one date');

    const newSchedule = { type, label, time, active };
    if (type === 'recurring')      newSchedule.days  = [...days];
    if (type === 'specific_date')  newSchedule.date  = date;
    if (type === 'specific_dates') newSchedule.dates = [...dates];

    setActivityForm(p => ({ ...p, schedules: [...p.schedules, newSchedule] }));
    setScheduleForm({ ...EMPTY_SCHEDULE_FORM });
    setDateInput('');
  };

  const removeSchedule = (idx) =>
    setActivityForm(p => ({ ...p, schedules: p.schedules.filter((_, i) => i !== idx) }));

  // toggle active on an already-added schedule
  const toggleScheduleActive = (idx) =>
    setActivityForm(p => ({
      ...p,
      schedules: p.schedules.map((s, i) => i === idx ? { ...s, active: s.active === false ? true : false } : s),
    }));

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-xl font-bold text-white">{editing ? 'Edit Activity' : 'New Activity'}</h2>
            <p className="text-white/60 text-sm mt-0.5">Fill in the activity details and schedules below</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="max-w-5xl mx-auto px-8 py-8 space-y-8">

            {/* ── BASIC INFO ── */}
            <Section title="Basic Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>Activity Name * <span className="font-normal text-gray-400 normal-case">(unique identifier)</span></Label>
                  <Input
                    value={activityForm.name}
                    onChange={v => setActivityForm(p => ({ ...p, name: v }))}
                    placeholder="e.g., yoga, hiit, spinning"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Used as a unique ID internally</p>
                </div>
                <div>
                  <Label>Activity Title *</Label>
                  <Input
                    value={activityForm.title}
                    onChange={v => setActivityForm(p => ({ ...p, title: v }))}
                    placeholder="e.g., Morning Yoga, HIIT Blast"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Display name shown to members</p>
                </div>
              </div>
            </Section>

            {/* ── PRICE PER VISIT ── */}
            <Section title="Pricing">
              <div className="max-w-xs">
                <Label>Price Per Visit (KES) <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">KES</span>
                  <input
                    type="number"
                    min="0"
                    value={activityForm.pricePerVisit}
                    onChange={e => setActivityForm(p => ({ ...p, pricePerVisit: e.target.value }))}
                    placeholder="e.g., 500"
                    className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Leave empty or 0 if price is included in membership</p>
              </div>
            </Section>

            {/* ── PHOTOS ── */}            <Section title="Activity Photos">
              <div className="flex flex-wrap gap-3">
                {activityForm.photos.map((url, i) => (
                  <div key={i} className="relative w-28 h-28 rounded-xl overflow-hidden border border-gray-200 group">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button type="button"
                        onClick={() => setActivityForm(p => ({ ...p, photos: p.photos.filter((_, idx) => idx !== i) }))}
                        className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {i === 0 && (
                      <span className="absolute bottom-0 left-0 right-0 text-center text-white py-0.5 bg-black/50" style={{ fontSize: '0.6rem' }}>Cover</span>
                    )}
                  </div>
                ))}
                <label
                  className="w-28 h-28 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all"
                  style={{ borderColor: uploading ? '#800020' : '#d1d5db' }}
                  onMouseEnter={e => !uploading && (e.currentTarget.style.borderColor = '#800020')}
                  onMouseLeave={e => !uploading && (e.currentTarget.style.borderColor = '#d1d5db')}>
                  <input ref={fileRef} type="file" multiple accept="image/*" className="hidden"
                    onChange={e => uploadActivityPhotos(Array.from(e.target.files))} disabled={uploading} />
                  {uploading
                    ? <div className="w-5 h-5 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                    : <><ArrowUpTrayIcon className="h-5 w-5 text-gray-400" /><span className="text-xs text-gray-400 mt-1">Upload</span></>
                  }
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-2">First photo will be used as the cover image.</p>
            </Section>

            {/* ── TRAINERS (optional, dynamic) ── */}
            <Section title={<>Trainers <span className="text-xs font-normal text-gray-400 ml-1 normal-case">(optional)</span></>}>
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={trainerInput}
                    onChange={e => setTrainerInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrainer(); } }}
                    placeholder="e.g., John Kamau"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <button type="button" onClick={addTrainer}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white flex-shrink-0 flex items-center gap-1.5 transition-all"
                  style={{ background: '#1a1a2e' }}>
                  <PlusIcon className="h-4 w-4" />
                  Add
                </button>
              </div>

              {activityForm.trainers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {activityForm.trainers.map((t, i) => (
                    <span key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium border"
                      style={{ background: '#fef2f2', color: '#800020', borderColor: '#fcd5de' }}>
                      <UserIcon className="h-3.5 w-3.5" />
                      {t}
                      <button type="button" onClick={() => removeTrainer(i)}
                        className="ml-0.5 hover:text-red-800 transition-colors">
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No trainers added. Press Enter or click Add after typing each name.</p>
              )}
            </Section>

            {/* ── WHAT TO EXPECT ── */}
            <Section title="What to Expect">
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={expectInput}
                  onChange={e => setExpectInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExpectItem(); } }}
                  placeholder="e.g., High intensity intervals, Full body workout…"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{ '--tw-ring-color': '#800020' }}
                />
                <button type="button" onClick={addExpectItem}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white flex-shrink-0 flex items-center gap-1.5 transition-all"
                  style={{ background: '#1a1a2e' }}>
                  <PlusIcon className="h-4 w-4" />
                  Add
                </button>
              </div>

              {activityForm.whatToExpect.length > 0 ? (
                <div className="space-y-2">
                  {activityForm.whatToExpect.map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5 group">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                      <button type="button" onClick={() => removeExpectItem(i)}
                        className="flex-shrink-0 ml-3 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No items added yet. Press Enter or click Add after typing each item.</p>
              )}
            </Section>

            {/* ── DESCRIPTION ── */}
            <Section title="Description Sections">
              <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                <div>
                  <Label>Section Title <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                  <Input value={descTitle} onChange={setDescTitle} placeholder="e.g., About this class" />
                </div>
                <div>
                  <Label>Section Content <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                  <textarea
                    value={descContent}
                    onChange={e => setDescContent(e.target.value)}
                    rows={3}
                    placeholder="Describe this activity, what to expect, requirements…"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 transition-all bg-white"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <div className="flex justify-end">
                  {editingDescIdx !== null ? (
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => { setDescTitle(''); setDescContent(''); setEditingDescIdx(null); }}
                        className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-all">
                        Cancel
                      </button>
                      <button type="button" onClick={updateDescSection}
                        className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
                        style={{ background: '#800020' }}>
                        Update Section
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={addDescSection}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                      style={{ background: '#1a1a2e' }}>
                      <PlusIcon className="h-4 w-4" />
                      Add Section
                    </button>
                  )}
                </div>
              </div>

              {activityForm.description.length > 0 ? (
                <div className="space-y-3">
                  {activityForm.description.map((sec, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {sec.title   && <p className="font-semibold text-gray-900 text-sm mb-1">{sec.title}</p>}
                          {sec.content && <p className="text-sm text-gray-500 leading-relaxed">{sec.content}</p>}
                          {!sec.title && !sec.content && <p className="text-sm text-gray-400 italic">Empty section</p>}
                        </div>
                        <div className="flex gap-2 ml-4 flex-shrink-0">
                          <button type="button" onClick={() => editDescSection(i)} className="text-blue-400 hover:text-blue-600 transition-colors">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => removeDescSection(i)} className="text-red-400 hover:text-red-600 transition-colors">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No description sections added yet. Title and content are both optional per section.</p>
              )}
            </Section>

            {/* ── SCHEDULES ── */}
            <Section title="Schedules">

              {/* Builder */}
              <div className="bg-gray-50 rounded-xl p-5 mb-5 space-y-5">
                <p className="text-sm font-semibold text-gray-700">Add a Schedule</p>

                {/* Type picker */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {SCHEDULE_TYPES.map(type => (
                    <button key={type.key} type="button"
                      onClick={() => setScheduleForm(p => ({ ...p, type: type.key, days: [], date: '', dates: [] }))}
                      className="p-3 rounded-xl border-2 text-left transition-all"
                      style={{
                        borderColor: scheduleForm.type === type.key ? '#800020' : '#e5e7eb',
                        background:  scheduleForm.type === type.key ? '#fef2f2' : '#fff',
                      }}>
                      <div className="mb-1.5" style={{ color: '#800020' }}>{type.icon}</div>
                      <p className="text-sm font-semibold text-gray-800">{type.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{type.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Label */}
                <div>
                  <Label>Schedule Label <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                  <Input value={scheduleForm.label} onChange={v => setScheduleForm(p => ({ ...p, label: v }))}
                    placeholder="e.g., Morning Session, Beginner Class" />
                </div>

                {/* Time */}
                <div>
                  <Label>Time *</Label>
                  <div className="relative">
                    <ClockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="time" value={scheduleForm.time}
                      onChange={e => setScheduleForm(p => ({ ...p, time: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
                      style={{ '--tw-ring-color': '#800020' }} />
                  </div>
                </div>

                {/* Active toggle for new schedule */}
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={scheduleForm.active !== false}
                      onChange={e => setScheduleForm(p => ({ ...p, active: e.target.checked }))}
                      className="sr-only peer" />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-700" />
                    <span className="ml-2 text-sm text-gray-600">
                      {scheduleForm.active !== false ? 'Schedule Active' : 'Schedule Inactive'}
                    </span>
                  </label>
                </div>

                {/* Recurring — days */}
                {scheduleForm.type === 'recurring' && (
                  <div>
                    <Label>Days of the Week *</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {DAYS_OF_WEEK.map(day => (
                        <button key={day.key} type="button" onClick={() => toggleDay(day.key)}
                          className="px-4 py-2 rounded-xl text-sm font-semibold transition-all border-2"
                          style={{
                            borderColor: scheduleForm.days.includes(day.key) ? '#800020' : '#e5e7eb',
                            background:  scheduleForm.days.includes(day.key) ? '#800020' : '#fff',
                            color:       scheduleForm.days.includes(day.key) ? '#fff'    : '#6b7280',
                          }}>
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specific date */}
                {scheduleForm.type === 'specific_date' && (
                  <div>
                    <Label>Date *</Label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="date" value={scheduleForm.date}
                        onChange={e => setScheduleForm(p => ({ ...p, date: e.target.value }))}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
                        style={{ '--tw-ring-color': '#800020' }} />
                    </div>
                  </div>
                )}

                {/* Multiple dates */}
                {scheduleForm.type === 'specific_dates' && (
                  <div>
                    <Label>Dates *</Label>
                    <div className="flex gap-2 mb-2">
                      <div className="relative flex-1">
                        <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input type="date" value={dateInput} onChange={e => setDateInput(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
                          style={{ '--tw-ring-color': '#800020' }} />
                      </div>
                      <button type="button" onClick={addDateToList}
                        className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white flex-shrink-0"
                        style={{ background: '#1a1a2e' }}>
                        Add Date
                      </button>
                    </div>
                    {scheduleForm.dates.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {scheduleForm.dates.map((d, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg font-medium"
                            style={{ background: '#fef2f2', color: '#800020' }}>
                            {new Date(d + 'T00:00:00').toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}
                            <button type="button"
                              onClick={() => setScheduleForm(p => ({ ...p, dates: p.dates.filter((_, idx) => idx !== i) }))}>
                              <XMarkIcon className="h-3 w-3 hover:text-red-700" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button type="button" onClick={addSchedule}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
                  <PlusIcon className="h-4 w-4" />
                  Add Schedule
                </button>
              </div>

              {/* Saved schedules */}
              {activityForm.schedules.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Added Schedules ({activityForm.schedules.length})
                  </p>
                  {activityForm.schedules.map((sch, i) => (
                    <div key={i}
                      className={`flex items-center justify-between bg-white border rounded-xl p-4 transition-all ${sch.active === false ? 'border-gray-200 opacity-60' : 'border-gray-200 hover:shadow-sm'}`}>
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: sch.active === false ? '#f3f4f6' : '#fef2f2' }}>
                          {sch.type === 'recurring'
                            ? <BoltIcon     className="h-4 w-4" style={{ color: sch.active === false ? '#9ca3af' : '#800020' }} />
                            : <CalendarIcon className="h-4 w-4" style={{ color: sch.active === false ? '#9ca3af' : '#800020' }} />
                          }
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-800">
                              {sch.label || SCHEDULE_TYPES.find(t => t.key === sch.type)?.label}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sch.active === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                              {sch.active === false ? 'Inactive' : 'Active'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{formatScheduleSummary(sch)}</p>
                          {sch.type === 'recurring' && sch.days?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {sch.days.map(d => (
                                <span key={d} className="text-xs px-2 py-0.5 rounded-md font-medium"
                                  style={{ background: '#fef2f2', color: '#800020' }}>
                                  {DAYS_OF_WEEK.find(dw => dw.key === d)?.label}
                                </span>
                              ))}
                            </div>
                          )}
                          {sch.type === 'specific_dates' && sch.dates?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {sch.dates.map((d, di) => (
                                <span key={di} className="text-xs px-2 py-0.5 rounded-md bg-gray-100 text-gray-600">
                                  {new Date(d + 'T00:00:00').toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Per-schedule actions */}
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        <button type="button" onClick={() => toggleScheduleActive(i)}
                          title={sch.active === false ? 'Activate schedule' : 'Deactivate schedule'}
                          className="w-8 h-8 flex items-center justify-center rounded-lg transition-all"
                          style={{
                            background: sch.active === false ? '#f0fdf4' : '#fef9c3',
                            color:      sch.active === false ? '#16a34a' : '#ca8a04',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                          {sch.active === false ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
                        </button>
                        <button type="button" onClick={() => removeSchedule(i)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No schedules added yet. Use the builder above to add schedules.</p>
              )}
            </Section>

            {/* ── VISIBILITY ── */}
            <Section title="Visibility">
              <div className="flex items-center gap-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={activityForm.active}
                    onChange={e => setActivityForm(p => ({ ...p, active: e.target.checked }))}
                    className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-700" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {activityForm.active ? 'Active — visible to members' : 'Inactive — hidden from members'}
                  </span>
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-2">Inactive activities won't appear on the member-facing view.</p>
            </Section>

          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button type="button" onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={onSubmit} disabled={loading}
            className="px-10 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
            {loading ? 'Saving…' : editing ? 'Update Activity' : 'Create Activity'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{children}</label>
);

const Input = ({ value, onChange, type = 'text', placeholder, required, disabled }) => (
  <input
    type={type}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    required={required}
    disabled={disabled}
    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all disabled:bg-gray-50 disabled:text-gray-400"
    style={{ '--tw-ring-color': '#800020' }}
  />
);

const Section = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <p className="text-base font-bold text-gray-900 whitespace-nowrap">{title}</p>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
    {children}
  </div>
);

export default ActivityManagement;